import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { deleteCookie, getCookie } from 'src/common/helpers'

const ClientDashboardLoginContext = createContext(null)

export const ClientDashboardLoginProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [openedFromShowFormCookie, setOpenedFromShowFormCookie] =
    useState(false)

  const openLoginOverlay = useCallback(() => {
    setOpenedFromShowFormCookie(false)
    setIsOpen(true)
  }, [])

  const closeLoginOverlay = useCallback(() => {
    setOpenedFromShowFormCookie(false)
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const showFormCookie = getCookie('showForm')
    if (showFormCookie === 'true') {
      deleteCookie('showForm', { path: '/' })
      setOpenedFromShowFormCookie(true)
      setIsOpen(true)

      return
    }

    const params = new URLSearchParams(window.location.search)
    if (params.get('showLogin') === 'true') {
      setIsOpen(true)
    }

    // capture phase fires before Next.js router.push() in the element's own onClick
    const handleShowLoginClick = (e) => {
      const anchor = e.target.closest('a')
      if (!anchor) return
      try {
        const url = new URL(anchor.href)
        if (url.searchParams.get('showLogin')?.startsWith('true')) {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen(true)
        }
      } catch {
        // non-parseable href, ignore
      }
    }

    document.addEventListener('click', handleShowLoginClick, true)

    return () =>
      document.removeEventListener('click', handleShowLoginClick, true)
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      openLoginOverlay,
      closeLoginOverlay,
      openedFromShowFormCookie,
    }),
    [isOpen, openLoginOverlay, closeLoginOverlay, openedFromShowFormCookie]
  )

  return (
    <ClientDashboardLoginContext.Provider value={value}>
      {children}
    </ClientDashboardLoginContext.Provider>
  )
}

export const useClientDashboardLogin = () => {
  const ctx = useContext(ClientDashboardLoginContext)
  if (!ctx) {
    return {
      isOpen: false,
      openLoginOverlay: () => {},
      closeLoginOverlay: () => {},
      openedFromShowFormCookie: false,
    }
  }

  return ctx
}
